import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, useMediaQuery, Dialog, DialogContent, Typography, Avatar, Link, Tooltip, IconButton, Chip } from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTheme } from '@mui/material/styles';
import { useAuthStore } from '../store/authStore';
import { urlClients, urlUsers } from '../config';
import FormDialog from '../components/ClientCard';
import { fetchData, fetchDataPromise } from '../services/ApiServices';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import CustomTooltip from '../components/CustomTooltip';
import HeaderComponent from '../components/HeaderComponent';
import { startOfMonth, format } from 'date-fns';
import * as MyCollections from "../services/MyCollections";
import { useThemeContext } from '../components/contexts/ThemeContext';
import Controls from '../components/controls/Controls';
import FinancialStatements from './FinancialStatements';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))
const initialValue = MyCollections.clientCardInitialValues;

const ClientCellRendererStartAccounting = (props) => {
  const { data, darkTheme, onUpdate } = props;
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  if (!data) return null;

  const companyWorkingName = data.companyWorkingName || '';
  const contact = data.clientContactDTO || data.clientContactDTOs?.[0];
  const rawRep = contact?.name;
  const representative = rawRep && rawRep !== 'null null' ? rawRep : null;
  const phone = contact?.phones?.[0]?.number || null;
  const email = contact?.emails?.[0]?.emailAddress || null;
  const startDate = data.cooperationHistoryDTO?.cooperationStartDate || null;
  const accountManager = data.accountManagerFullName || null;
  const firstAccountant = data.accountantDTOs?.[0];
  const accountantName = firstAccountant ? `${firstAccountant.name || ''} ${firstAccountant.surname || ''}`.trim() : null;

  const getInitials = (name) => {
    if (!name) return 'K';
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleCopy = (e, text, setCopiedFn) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedFn(true);
      setTimeout(() => setCopiedFn(false), 2000);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 1,
        px: 0.5,
        gap: 0.75,
        lineHeight: 1.3,
      }}
    >
      {/* Row 1: Avatar + Company Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            fontWeight: 700,
            bgcolor: darkTheme ? 'rgba(56, 189, 248, 0.15)' : 'rgba(37, 99, 235, 0.1)',
            color: darkTheme ? '#38bdf8' : '#2563eb',
            border: `1px solid ${darkTheme ? 'rgba(56, 189, 248, 0.3)' : 'rgba(37, 99, 235, 0.25)'}`,
            flexShrink: 0,
            boxShadow: darkTheme ? '0 2px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {getInitials(companyWorkingName)}
        </Avatar>

        <Tooltip title="Kliknij, aby edytować kartę klienta" arrow placement="top">
          <Typography
            component="span"
            onClick={() => onUpdate && onUpdate(data)}
            sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              color: darkTheme ? '#60a5fa' : '#1d4ed8',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: darkTheme ? '#93c5fd' : '#2563eb',
                textDecoration: 'underline',
              },
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {companyWorkingName}
          </Typography>
        </Tooltip>
      </Box>

      {/* Row 2: Representative, Phone & Email Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.4,
          pl: 1,
          borderLeft: `2px solid ${darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(203, 213, 225, 0.7)'}`,
          ml: 0.75,
        }}
      >
        {/* Representative */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PersonOutlinedIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: representative ? (darkTheme ? '#e2e8f0' : '#334155') : (darkTheme ? '#64748b' : '#94a3b8'),
              fontStyle: representative ? 'normal' : 'italic',
              fontWeight: representative ? 500 : 400,
            }}
          >
            {representative || 'Brak reprezentanta'}
          </Typography>
        </Box>

        {/* Phone */}
        {phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Link
              underline="hover"
              href={`tel:${phone}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontSize: '0.75rem',
                color: darkTheme ? '#38bdf8' : '#0284c7',
                fontWeight: 600,
                fontFamily: 'monospace, monospace',
                letterSpacing: '0.02em',
                '&:hover': { color: darkTheme ? '#7dd3fc' : '#0369a1' }
              }}
            >
              {phone}
            </Link>
            <Tooltip title={copiedPhone ? "Skopiowano!" : "Kopiuj telefon"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, phone, setCopiedPhone)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedPhone ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: darkTheme ? '#cbd5e1' : '#475569',
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedPhone ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Email */}
        {email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MailOutlineIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Link
              underline="hover"
              href={`mailto:${email}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontSize: '0.75rem',
                color: darkTheme ? '#38bdf8' : '#0284c7',
                fontWeight: 500,
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': { color: darkTheme ? '#7dd3fc' : '#0369a1' }
              }}
            >
              {email}
            </Link>
            <Tooltip title={copiedEmail ? "Skopiowano!" : "Kopiuj email"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, email, setCopiedEmail)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedEmail ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: darkTheme ? '#cbd5e1' : '#475569',
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedEmail ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Row 3: Metadata (Agreement date, Account Manager, Accountant) */}
      {(startDate || accountManager || accountantName) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.4,
            pl: 1,
            mt: 0.25,
          }}
        >
          {startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarTodayIcon sx={{ fontSize: 13, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: '0.725rem', color: darkTheme ? '#cbd5e1' : '#475569' }}>
                umowa z dnia: <strong>{startDate}</strong>
              </Typography>
            </Box>
          )}

          {accountManager && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AssignmentIndOutlinedIcon sx={{ fontSize: 13, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: '0.725rem', color: darkTheme ? '#cbd5e1' : '#475569' }}>
                opiekun: <strong>{accountManager}</strong>
              </Typography>
            </Box>
          )}

          {accountantName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <BadgeIcon sx={{ fontSize: 13, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: '0.725rem', color: darkTheme ? '#cbd5e1' : '#475569' }}>
                księgowy: <strong>{accountantName}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const CooperationStatusRendererStartAccounting = ({ value, data, darkTheme }) => {
  let label = "Brak";
  let color = darkTheme ? '#94a3b8' : '#64748b';
  let bgcolor = darkTheme ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.08)';
  let border = darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)';

  if (value === true || value === "Aktywny" || value === "Aktywna") {
    label = "Aktywny";
    color = darkTheme ? '#34d399' : '#059669';
    bgcolor = darkTheme ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)';
    border = darkTheme ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.25)';
  } else if (data?.cooperationHistoryDTO?.suspended || value === "Zawieszony" || value === "Zawieszona") {
    label = "Zawieszony";
    color = darkTheme ? '#fbbf24' : '#d97706';
    bgcolor = darkTheme ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)';
    border = darkTheme ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.25)';
  } else if (data?.cooperationHistoryDTO?.cooperationEndDate || value === "Zakończona" || value === "Zakończony") {
    label = "Zakończony";
    color = darkTheme ? '#f87171' : '#dc2626';
    bgcolor = darkTheme ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)';
    border = darkTheme ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.25)';
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 600,
        color,
        bgcolor,
        border: `1px solid ${border}`,
      }}
    />
  );
};

const DateBadgeRendererStartAccounting = ({ value, darkTheme }) => {
  if (!value || value === 'brak') {
    return (
      <Typography
        variant="caption"
        sx={{
          color: darkTheme ? '#64748b' : '#94a3b8',
          fontStyle: 'italic',
          fontSize: '0.75rem',
        }}
      >
        wstaw datę...
      </Typography>
    );
  }
  return (
    <Chip
      icon={<CalendarTodayIcon sx={{ fontSize: '12px !important', color: darkTheme ? '#34d399 !important' : '#059669 !important' }} />}
      label={value}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 600,
        fontFamily: 'monospace, monospace',
        bgcolor: darkTheme ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.12)',
        color: darkTheme ? '#34d399' : '#047857',
        border: `1px solid ${darkTheme ? 'rgba(16, 185, 129, 0.35)' : 'rgba(16, 185, 129, 0.25)'}`,
        '& .MuiChip-icon': {
          ml: 0.5,
        }
      }}
    />
  );
};

const CommentRendererStartAccounting = ({ value, darkTheme, placeholder = "wprowadź..." }) => {
  if (!value || value === 'brak...') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', opacity: 0.45, width: '100%', px: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.725rem', fontStyle: 'italic', color: darkTheme ? '#94a3b8' : '#64748b' }}>
          {placeholder}
        </Typography>
      </Box>
    );
  }

  const maxLength = 70;
  const truncated = value.length > maxLength ? value.substring(0, maxLength) + '...' : value;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, px: 0.5, height: '100%', width: '100%' }}>
      <ChatBubbleOutlineIcon sx={{ fontSize: 13, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.725rem',
          color: darkTheme ? '#cbd5e1' : '#334155',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.3,
          textAlign: 'left',
        }}
      >
        {truncated}
      </Typography>
    </Box>
  );
};

const TerminationReasonRendererStartAccounting = ({ params, darkTheme }) => {
  if (!params.data) return null;
  const reason = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperation;
  if (!reason) return null;

  var reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;
  const endMonth = params.data.cooperationHistoryDTO?.cooperationEndDate?.slice(0, -3);
  if (endMonth && !params.data.cooperationHistoryDTO?.ended) {
    reasonId = 101;
  }

  let color = darkTheme ? '#cbd5e1' : '#334155';
  let bg = darkTheme ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.1)';
  let border = darkTheme ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.25)';

  if (reasonId === 11) {
    color = darkTheme ? '#f87171' : '#dc2626';
    bg = darkTheme ? 'rgba(248, 113, 113, 0.18)' : 'rgba(220, 38, 38, 0.1)';
    border = darkTheme ? 'rgba(248, 113, 113, 0.35)' : 'rgba(220, 38, 38, 0.25)';
  } else if (reasonId === 101) {
    color = darkTheme ? '#fbbf24' : '#d97706';
    bg = darkTheme ? 'rgba(251, 191, 36, 0.18)' : 'rgba(217, 119, 6, 0.1)';
    border = darkTheme ? 'rgba(251, 191, 36, 0.35)' : 'rgba(217, 119, 6, 0.25)';
  } else if (reasonId === 1) {
    color = darkTheme ? '#94a3b8' : '#64748b';
    bg = darkTheme ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.08)';
    border = darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.2)';
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', py: 0.5 }}>
      <Chip
        label={reason}
        size="small"
        sx={{
          height: 'auto',
          minHeight: 24,
          py: 0.25,
          fontSize: '0.725rem',
          fontWeight: 700,
          bgcolor: bg,
          color: color,
          border: `1px solid ${border}`,
          borderRadius: '6px',
          whiteSpace: 'normal',
          '& .MuiChip-label': {
            px: 1,
            py: 0.25,
            whiteSpace: 'normal',
            lineHeight: 1.25,
            textAlign: 'center',
          }
        }}
      />
    </Box>
  );
};

function StartAccounting() {
  const navigate = useNavigate();
  const gridRef = useRef();
  const requestQueueRef = useRef({});
  const { roles } = useAuthStore();

  const token = useAuthStore().token;
  const contactId = useAuthStore().contactId;
  const userName = useAuthStore().fullName;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableData2, setTableData2] = useState([]);
  const [tableData3, setTableData3] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState((JSON.parse(JSON.stringify(initialValue))))
  const classes = useStyles();
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [start, setStart] = useState(true);
  const [end, setEnd] = useState(true);
  const [value, setValue] = React.useState('one');
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [financialStatementsDialogOpen, setFinancialStatementsDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  const handleUpdate = useCallback(async (oldData) => {
    try {
      const clientData = await fetchDataPromise(urlClients + "/" + oldData.id, null, null, console.error, token);
      setFormData(clientData ? clientData : initialValue);
      setOpen(true);
    } catch (error) {
      console.error('Błąd podczas aktualizacji:', error);
    }
  }, [token]);

  const filterParamsMonth = useMemo(() => ({
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      if (!cellValue) return -1;

      let cellDate = new Date(cellValue);
      if (isNaN(cellDate)) return -1;

      const filterMonthYear = format(startOfMonth(filterLocalDateAtMidnight), "yyyy-MM");
      const cellMonthYear = format(startOfMonth(cellDate), "yyyy-MM");

      return cellMonthYear.localeCompare(filterMonthYear);
    },
    minValidYear: 2020,
    maxValidYear: new Date().getFullYear(),
    inRangeFloatingFilterDateFormat: "MMM yyyy",
  }), []);


  const [coordinatorsList, setCoordinatorsList] = useState([{ key: null, value: 'wybierz' }]);

  useEffect(() => {
    fetchDataPromise(urlUsers + '/coordinators', null, null, console.error, token)
      .then(data => {
        if (data && Array.isArray(data)) {
          const mappedData = data.map(item => ({
            key: item.id,
            value: item.firstName && item.lastName ? `${item.firstName.charAt(0)}. ${item.lastName}` : (item.lastName || 'Brak')
          }));
          setCoordinatorsList([{ key: null, value: 'wybierz' }, ...mappedData]);
        } else if (data) {
          console.warn('API returned non-array data:', data);
        }
      })
      .catch(err => console.error("Error fetching coordinators:", err));
  }, [token]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Klient",
      lockPosition: 'left',
      minWidth: 240,
      editable: false,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => {
        return params.data?.companyWorkingName || '';
      },
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const nameA = nodeA?.data?.companyWorkingName?.toLowerCase() || '';
        const nameB = nodeB?.data?.companyWorkingName?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
      cellStyle: {
        textAlign: 'left',
      },
      cellRenderer: (params) => (
        <ClientCellRendererStartAccounting
          data={params.data}
          darkTheme={darkTheme}
          onUpdate={handleUpdate}
        />
      )
    },
    {
      headerName: "Start od",
      field: "startAccountingMonth",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      sortable: true,
      comparator: (valueA, valueB) => {
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;
        return String(valueA).localeCompare(String(valueB));
      },
      lockPosition: 'left',
      minWidth: 85,
      headerComponentParams: { text: "Start od" },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      valueGetter: (params) => {
        if (!params.data) return '';
        const d = params.data;
        return (
          d.startAccountingMonth ||
          d.startMonth ||
          d.startDate ||
          d.accountingStartDate ||
          d.accountingStartMonth ||
          d.cooperationHistoryDTO?.startDate ||
          (d.cooperationHistoryDTOs && d.cooperationHistoryDTOs[0]?.startDate) ||
          (d.accountantDTOs && d.accountantDTOs[0]?.startDate) ||
          ''
        );
      },
      valueFormatter: (params) => {
        if (!params.value) return '—';
        const val = String(params.value);
        if (val.length >= 7 && val.includes('-')) {
          return val.slice(0, 7);
        }
        return val;
      }
    },
    {
      headerName: "Sugestie handlowca",
      field: "tradersComments",
      editable: false,
      lockPosition: 'left',
      minWidth: 120,
      cellRenderer: (params) => (
        <CommentRendererStartAccounting
          value={params.value}
          darkTheme={darkTheme}
          placeholder="brak..."
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      tooltipValueGetter: (params) => params.value || '',
    },
    {
      headerName: "Dodatkowe uwagi",
      field: "additionalNotes",
      lockPosition: 'left',
      tooltipField: "tooltip",
      minWidth: 110,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: { maxLength: 999 },
      cellRenderer: (params) => (
        <CommentRendererStartAccounting
          value={params.value}
          tooltip={params.data?.tooltip}
          darkTheme={darkTheme}
          placeholder="wprowadź..."
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Mail powitalny",
      field: "welcomeOnBoardMailDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 100,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Rozmowa zapoznawcza",
      field: "introductionInterviewDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 100,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Druki startowe",
      field: "isStarterFormsSent",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 75,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "3 pytania przed weekendem",
      field: "isTreeQuestionsBeforeWeekend",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 85,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Księgowa/y",
      field: "currentAccountantName",
      editable: false,
      lockPosition: 'left',
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0) {
          let accountants = params.data.accountantDTOs;
          let acc = accountants[0];
          return (
            <div style={{
              lineHeight: '1.5',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <strong>{acc.surname} {acc.name}</strong><br />
              <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>Obsługa od: {acc.startDate.slice(0, 7)}</span>
            </div>
          );
        } else {
          return (
            <div style={{
              lineHeight: '1.5',
              justifyContent: 'center',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <div>
                <button
                  className="table-action-btn"
                  onClick={() => handleUpdate(params.data)}
                >
                  <i className="fas fa-user-plus"></i>
                  Przydziel
                </button>
              </div>
            </div>
          );
        }
      },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        };
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      minWidth: 120,
    },
    {
      headerName: "Start księgowości wysł./zakol.",
      field: "accountingStartSentQueuedDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 100,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Scanye/KSeF",
      field: "isScanyeKsef",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 75,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Przypisany cennik",
      field: "isTariffAssigned",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 70,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Faktura za pierwszy miesiąc",
      field: "isFirstInvoiceIssued",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 85,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Dane w Fakturowni",
      field: "isEmailAndPhoneInFakturownia",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 85,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Polityka rachunkowości",
      field: "isAccountingPolicy",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      headerClass: isArchiveView ? 'no-separator' : null,
      minWidth: 90,
      suppressMenu: true,
      filter: false,
      sortable: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Akcja",
      field: "startAccountingArchivedDate",
      lockPosition: 'left',
      minWidth: 115,
      editable: false,
      hide: (!roles?.includes('ADMIN') && !roles?.includes('ADMIN_SUPERVISOR') && !roles?.includes('MANAGER')) || isArchiveView,
      headerClass: 'no-separator',
      cellRenderer: function (params) {
        if (!params.data) return null;
        if ((params.data?.accountantDTOs && params.data.accountantDTOs.length > 0)
          && params.data.isEmailAndPhoneInFakturownia && params.data.isFirstInvoiceIssued && params.data.isTariffAssigned
          && params.data.accountingStartSentQueuedDate && params.data.isStarterFormsSent && params.data.isTreeQuestionsBeforeWeekend && params.data.introductionInterviewDate
          && params.data.welcomeOnBoardMailDate && params.data.isAccountingPolicy && params.data.isScanyeKsef) {
          return (
            <div style={{ lineHeight: '1.7' }}>
              <div>
                <button
                  className="table-action-btn"
                  onClick={() => handleArchive(params)}
                >
                  <i className="fas fa-archive"></i>
                  Archiwizuj
                </button>
              </div>
            </div>
          );
        } else {
          return (
            null
          )
        }
      },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
  ], [isArchiveView, darkTheme]);

  const others = useMemo(() => [
    {
      headerName: "Klient",
      lockPosition: 'left',
      minWidth: 240,
      editable: false,
      filter: 'agTextColumnFilter', // Aktywujemy klasyczny filtr tekstowy
      filterValueGetter: (params) => {
        return params.data?.companyWorkingName || '';
      },
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {

        const nameA = nodeA?.data?.companyWorkingName?.toLowerCase() || '';
        const nameB = nodeB?.data?.companyWorkingName?.toLowerCase() || '';

        return nameA.localeCompare(nameB);
      },
      cellStyle: {
        textAlign: 'left',
      },
      cellRenderer: (params) => (
        <ClientCellRendererStartAccounting
          data={params.data}
          darkTheme={darkTheme}
          onUpdate={handleUpdate}
        />
      )
    },
    {
      headerName: "Status klienta",
      field: "isActive",
      lockPosition: 'left',
      editable: false,
      minWidth: 100,
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (params.value) {
          return "Aktywny"
        }
        else if (params.data.cooperationHistoryDTO?.suspended) {
          return "Zawieszony"
        }
        else if (params.data.cooperationHistoryDTO?.cooperationEndDate) {
          return <div style={{ lineHeight: '1.3' }}>Zakończona <br /> współpraca</div>
        }
        else {
          return "";
        }
      },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Przyczyna i komentarz",
      field: "additionalNotesAccountantChange",
      lockPosition: 'left',
      tooltipField: "tooltip1",
      minWidth: 110,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      filter: 'agTextColumnFilter',
      cellEditorParams: { maxLength: 999 },
      cellRenderer: (params) => (
        <CommentRendererStartAccounting
          value={params.value}
          tooltip={params.data?.tooltip1}
          darkTheme={darkTheme}
          placeholder="wprowadź..."
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Dotychczasowa księgowa/y",
      field: "currentAccountantName",
      editable: false,
      lockPosition: 'left',
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0) {
          let accountants;
          if (params.data.isAccountantChanged && !params.data.accountantDTOs[0].endDate) {
            accountants = params.data.accountantDTOs.slice(1, 4);
            return (
              <div style={{ lineHeight: '1.7' }}>
                {accountants.map(acc => (
                  <div key={acc.id}>
                    {accountants.length === 1 ? <br /> : <></>}
                    <strong>{acc.surname} {acc.name}</strong><br />
                    Obsługa od: {acc.endDate ? <> {acc.startDate.slice(0, 7)} <br /> do: {acc.endDate.slice(0, 7)} <br /><br /></> : <><br />{acc.startDate.slice(0, 7)}<br /><br /></>}
                  </div>
                ))}
              </div>
            );
          } else {
            accountants = params.data.accountantDTOs.slice(0, 4);
            return (
              <div style={{ lineHeight: '1.7' }}>
                {accountants.map(acc => (
                  <div key={acc.id}>
                    {acc.endDate ? <></> : <br />}
                    <strong>{acc.surname} {acc.name}</strong><br />
                    Obsługa od: {acc.endDate ? <> {acc.startDate.slice(0, 7)} <br /> do: {acc.endDate.slice(0, 7)} <br /><br /></> : <><br />{acc.startDate.slice(0, 7)}<br /><br /></>}
                  </div>
                ))}
              </div>
            );
          }
        } else {
          return null;
        }
      },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          overflow: 'hidden',
        };
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      minWidth: 130,
    },
    {
      headerName: "Nowa księgowa/y",
      field: "currentAccountantName",
      editable: false,
      lockPosition: 'left',
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0) {
          let accountants = params.data.accountantDTOs;
          let acc = accountants[0];
          if (acc.endDate) {
            return (
              <div style={{ lineHeight: '1.7', justifyContent: 'center' }}>
                <div>
                  <button
                    className="table-action-btn"
                    onClick={() => handleUpdate(params.data)}
                  >
                    <i className="fas fa-user-plus"></i>
                    Przydziel
                  </button>
                </div>
              </div>
            );
          } else {
            return (
              <div style={{ lineHeight: '1.7' }}>
                <strong>{acc.surname} {acc.name}</strong><br />
                Obsługa od:<br /> {acc.startDate.slice(0, 7)}
              </div>
            );
          }
        } else {
          return (
            <div style={{ lineHeight: '1.7', justifyContent: 'center' }}>
              <div>
                <button
                  className="table-action-btn"
                  onClick={() => handleUpdate(params.data)}
                >
                  <i className="fas fa-user-plus"></i>
                  Przydziel
                </button>
              </div>
            </div>
          );
        }
      },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          overflow: 'hidden',
        };
        if (params.data.accountantDTOs && params.data.accountantDTOs.length > 0 && !params.data.accountantDTOs[0].endDate) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      minWidth: 130,
    },
    {
      headerName: "Mail zmiana księgowej",
      field: "changeAccountantMailDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 100,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Wysłane UPL i OPL",
      field: "newUplSendDate",
      headerClass: isArchiveView ? 'no-separator' : null,
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 95,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Telefon do klienta",
      field: "phoneCallDate",
      headerClass: isArchiveView ? 'no-separator' : null,
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 95,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
      valueFormatter: (params) => params.data && params.value ? params.value : 'wstaw datę',
    },
    {
      headerName: "Uzupełnione informacje o firmie",
      field: "isCompanyInfoCompleted",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 75,
      suppressMenu: true,
      filter: false,
      sortable: false,
      headerTooltip: "Proszę zaktualizować opis firmy na Karcie Klienta: czym zajmuje się firma, czy posiada pojazdy, czy były płace, rmk, pożyczki, inne?",
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "Uzupełnione informacje o firmie", showIconNextToText: true },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Sprawozdanie finansowe",
      lockPosition: 'left',
      minWidth: 140,
      editable: false,
      sortable: false,
      filter: false,
      cellRenderer: function (params) {
        if (!params.data) return null;

        const handleClick = () => {
          setSelectedClientId(params.data.id);
          setFinancialStatementsDialogOpen(true);
        };

        return (
          <div style={{ lineHeight: '1.7', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <div>
              <button
                className="table-action-btn"
                onClick={handleClick}
              >
                Ustaw wykonawcę
              </button>
            </div>
          </div>
        );
      },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Akcja",
      field: "startAccountingArchivedDate",
      lockPosition: 'left',
      minWidth: 120,
      editable: false,
      hide: (!roles?.includes('ADMIN') && !roles?.includes('ADMIN_SUPERVISOR') && !roles?.includes('MANAGER')) || isArchiveView,
      headerClass: 'no-separator',
      cellRenderer: function (params) {
        if (!params.data) return null;
        if ((params.data.accountantDTOs && params.data.accountantDTOs.length > 1)
          && params.data.newUplSendDate && params.data.changeAccountantMailDate && params.data.phoneCallDate && params.data.isCompanyInfoCompleted) {
          return (
            <div style={{ lineHeight: '1.7' }}>
              <div>
                <button
                  className="table-action-btn"
                  onClick={() => handleArchiveAfterAccountantChange(params)}
                >
                  <i className="fas fa-archive"></i>
                  Archiwizuj
                </button>
              </div>
            </div>
          );
        } else {
          return (
            null
          )
        }
      },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
  ], [isArchiveView, darkTheme, roles]);

  const ended = useMemo(() => [
    {
      headerName: "Klient",
      lockPosition: 'left',
      minWidth: 240,
      cellStyle: {
        textAlign: 'left',
      },
      filter: 'agTextColumnFilter', // Aktywujemy klasyczny filtr tekstowy
      filterValueGetter: (params) => {
        return params.data?.companyWorkingName || '';
      },
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {

        const nameA = nodeA?.data?.companyWorkingName?.toLowerCase() || '';
        const nameB = nodeB?.data?.companyWorkingName?.toLowerCase() || '';

        return nameA.localeCompare(nameB);
      },
      cellRenderer: (params) => (
        <ClientCellRendererStartAccounting
          data={params.data}
          darkTheme={darkTheme}
          onUpdate={handleUpdate}
        />
      )
    },
    {
      headerName: "Koniec współpracy",
      field: "cooperationHistoryDTO.cooperationEndDate",
      filter: 'agDateColumnFilter',
      filterParams: filterParamsMonth,
      sortable: true,
      lockPosition: 'left',
      minWidth: 120,
      editable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: function (params) {
        if (params.data.cooperationHistoryDTO && params.data.cooperationHistoryDTO.cooperationEndDate) {
          const date = new Date(params.data.cooperationHistoryDTO.cooperationEndDate);
          return date.toISOString().slice(0, 7); // Formatowanie do YYYY-MM
        } else {
          return "brak";
        }
      }
    },
    {
      headerName: "Przyczyna zakończenia",
      field: "cooperationHistoryDTO.reasonForTerminatingCooperation",
      lockPosition: 'left',
      minWidth: 120,
      cellRenderer: (params) => (
        <TerminationReasonRendererStartAccounting
          params={params}
          darkTheme={darkTheme}
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Komentarz do zakończenia",
      field: "endingComment",
      lockPosition: 'left',
      tooltipField: "tooltip2",
      minWidth: 140,
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "Komentarz zakończenia" },
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: { maxLength: 999 },
      cellRenderer: (params) => (
        <CommentRendererStartAccounting
          value={params.value}
          tooltip={params.data?.tooltip2}
          darkTheme={darkTheme}
          placeholder="wprowadź..."
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Kto koordynuje",
      field: "coordinatorId",
      lockPosition: 'left',
      cellEditor: 'agSelectCellEditor',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellEditorParams: (params) => ({
        values: coordinatorsList.map(coordinator => coordinator.key),
        formatValue: (key) => {
          const coordinator = coordinatorsList.find(c => c.key === key);
          return coordinator ? coordinator.value : key;
        },
      }),
      valueFormatter: function (params) {
        const coordinator = coordinatorsList.find(c => c.key === params.value);
        return coordinator ? coordinator.value : params.value;
      },
      valueGetter: function (params) {
        const coordinator = coordinatorsList.find(c => c.key === params.data.coordinatorId);
        return coordinator ? coordinator.value : 'wybierz';
      },
      valueSetter: function (params) {
        if (params.newValue !== params.data.coordinatorId) {
          params.data.coordinatorId = params.newValue;
          return true;
        }
        return false;
      },
      minWidth: 110,
      singleClickEdit: false,
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.data.coordinatorId) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Księgowa/y",
      field: "currentAccountantName",
      lockPosition: 'left',
      minWidth: 100,
      filter: 'agTextColumnFilter',
      editable: false,
      sortable: true,
      cellStyle: {
        color: darkTheme ? '#b0baee' : '#255c94',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
      autoHeight: true,
      valueGetter: function (params) {
        const accountants = params.data?.accountantDTOs;
        if (accountants?.length > 0) {
          return `${accountants[0]?.surname ?? ''} ${accountants[0]?.name ?? ''}`.trim();
        }
        return "brak";
      },
    },
    {
      headerName: "Kiedy złożyli wypowiedzenie",
      field: "terminationDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 85,
      cellStyle: function (params) {
        if (!params.data) return null;

        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        const style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        } else if (reasonId === 1) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }

        return style;
      },
      valueFormatter: function (params) {
        if (!params.data) return null;

        const { value, data } = params;
        const reasonId = data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        if (value) {
          return value;
        }

        return reasonId === 1 ? 'nie dotyczy' : 'wstaw datę';
      }
    },
    {
      headerName: "Mail zakończenie współpracy",
      field: "terminationEmailDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 85,
      cellStyle: function (params) {
        if (!params.data) return null;

        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        const style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        } else if (reasonId === 1) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }

        return style;
      },
      valueFormatter: function (params) {
        if (!params.data) return null;

        const { value, data } = params;
        const reasonId = data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        if (value) {
          return value;
        }

        return reasonId === 1 ? 'nie dotyczy' : 'wstaw datę';
      }
    },
    {
      headerName: "Termin na dokumenty",
      field: "deadlineForDocuments",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 85,
      cellStyle: function (params) {
        if (!params.data) return null;

        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        const style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        } else if (reasonId === 1 || reasonId === 51) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }

        return style;
      },
      valueFormatter: function (params) {
        if (!params.data) return null;

        const { value, data } = params;
        const reasonId = data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        if (value) {
          return value;
        }

        return reasonId === 1 || reasonId === 51 ? 'nie dotyczy' : 'wstaw datę';
      }
    },
    {
      headerName: "Przypominajka",
      field: "isReminderSent",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      headerTooltip: "Przypominajka",
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "Przypominajka" },
      cellStyle: function (params) {
        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        else if (reasonId === 1 || reasonId === 51) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }
        return style;
      },
    },
    {
      headerName: "Data wydania dokumentów",
      field: "documentsIssueDate",
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      minWidth: 85,
      cellStyle: function (params) {
        if (!params.data) return null;

        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        const style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        } else if (reasonId === 1 || reasonId === 51) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }

        return style;
      },
      valueFormatter: function (params) {
        if (!params.data) return null;

        const { value, data } = params;
        const reasonId = data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        if (value) {
          return value;
        }

        return reasonId === 1 || reasonId === 51 ? 'nie dotyczy' : 'wstaw datę';
      }
    },
    {
      headerName: "OPL (ksie + sup)",
      field: "isOpl",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      headerTooltip: "OPL (ksie + sup)",
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "OPL (ksie + sup)" },
      cellStyle: function (params) {
        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };

        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        else if (reasonId === 1 || reasonId === 51) {
          style.backgroundColor = darkTheme ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)';
          style.color = darkTheme ? '#94a3b8' : '#64748b';
        }
        return style;
      },
    },
    {
      headerName: "Odpięcie kont scanye/fakturownia/inne",
      field: "isFakturowniaAccountClosed",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      headerTooltip: "Odpięcie kont scanye/fakturownia/inne",
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "Odpięcie kont scanye/fakturownia/inne" },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Sprawdzone zaległości w płatnościach + wysłane upomnienie",
      field: "isCheckedArrearsInPayments",
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      lockPosition: 'left',
      minWidth: 75,
      suppressMenu: true,
      filter: false,
      sortable: false,
      headerTooltip: "Sprawdzone zaległości w płatnościach + wysłane upomnienie",
      headerComponent: HeaderComponent,
      headerComponentParams: { text: "Sprawdzone zaległości w płatnościach + wysłane upomnienie" },
      cellStyle: function (params) {
        if (!params.data) return null;
        var style = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        };
        if (params.value) {
          style.backgroundColor = darkTheme ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)';
        }
        return style;
      },
    },
    {
      headerName: "Sprawozdanie finansowe",
      field: "financialReportComment",
      headerClass: isArchiveView ? 'no-separator' : null,
      lockPosition: 'left',
      tooltipField: "tooltip3",
      minWidth: 110,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      filter: 'agTextColumnFilter',
      cellEditorParams: { maxLength: 999 },
      cellRenderer: (params) => (
        <CommentRendererStartAccounting
          value={params.value}
          tooltip={params.data?.tooltip3}
          darkTheme={darkTheme}
          placeholder="wprowadź..."
        />
      ),
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    {
      headerName: "Akcja",
      field: "endedCooperationArchivedDate",
      lockPosition: 'left',
      minWidth: 120,
      editable: false,
      hide: !roles?.includes('ADMIN') && !roles?.includes('ADMIN_SUPERVISOR') && !roles?.includes('MANAGER') || isArchiveView,
      headerClass: 'no-separator',
      cellRenderer: function (params) {
        if (!params.data) return null;

        const {
          coordinatorId,
          terminationDate,
          terminationEmailDate,
          deadlineForDocuments,
          documentsIssueDate,
          isReminderSent,
          isOpl,
          isFakturowniaAccountClosed,
          isCheckedArrearsInPayments
        } = params.data;

        const reasonId = params.data.cooperationHistoryDTO?.reasonForTerminatingCooperationId;

        // Jeśli reasonId === 1, ignorujemy więcej pól
        const shouldIgnoreTerminationFields = reasonId === 1;
        // Jeśli reasonId === 51, ignorujemy tylko pola związane z dokumentami
        const shouldIgnoreDocumentFields = reasonId === 51;

        // Sprawdzenie, czy wymagane pola są uzupełnione (uwzględniając wyjątki)
        const allFieldsFilled = coordinatorId &&
          (!shouldIgnoreTerminationFields ? terminationDate && terminationEmailDate : true) &&
          (!shouldIgnoreTerminationFields && !shouldIgnoreDocumentFields ?
            deadlineForDocuments && documentsIssueDate && isReminderSent && isOpl : true) &&
          isFakturowniaAccountClosed && isCheckedArrearsInPayments && params.data.financialReportComment && params.data.endingComment;

        return allFieldsFilled ? (
          <div style={{ lineHeight: '1.7' }}>
            <div>
              <button
                className="table-action-btn"
                onClick={() => handleArchiveEndedCooperation(params)}
              >
                <i className="fas fa-archive"></i>
                Archiwizuj
              </button>
            </div>
          </div>
        ) : null;
      },
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
  ], [isArchiveView, darkTheme, coordinatorsList]);

  const defaultColDef = {
    resizable: true,
    editable: !isArchiveView,
    filter: false,
    autoHeight: true,
    tooltipComponent: CustomTooltip,
    flex: 1
  }

  useEffect(() => {
    fetchClientsNotArchived();
  }, []);


  const fetchClientsNotArchived = async () => {
    setIsLoading(true); // 🔹 Włącz loading
    let url = `${urlClients}/allNotArchived`;
    try {
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));

      setTableData(rowData);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setIsLoading(false); // 🔹 Wyłącz loading po zakończeniu requestu
    }
  };

  const fetchClientsArchived = async () => {
    setIsLoading(true);
    let url = `${urlClients}/allArchived`;
    try {
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));

      rowData.isArchivedView = true;
      setTableData(rowData);
    } catch (error) {
      console.error("Error fetching archived clients:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsWithAccountantChanged = async () => {
    setIsLoading(true);
    try {
      let url = urlClients + '/allWithAccountantChange';
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));
      setTableData2(rowData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedClientsWithAccountantChanged = async () => {
    setIsLoading(true);
    try {
      let url = urlClients + '/allArchivedWithAccountantChange';
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));
      setTableData2(rowData);
      setIsArchiveView(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsWithEndedCooperation = async () => {
    setIsLoading(true);
    try {
      let url = urlClients + '/allWithEndedCooperation';
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));
      setTableData3(rowData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedClientsWithEndedCooperation = async () => {
    setIsLoading(true);
    try {
      let url = urlClients + '/allArchivedWithEndedCooperation';
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      const rowData = data.map(item => addTooltipField(item));
      setTableData3(rowData);
      setIsArchiveView(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };


  const getRowId = useMemo(() => {
    return (params) => {
      return params.data.id;
    };
  }, []);

  const onGridReady = (params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const processRowDataChange = async ({ data, colDef }) => {
    if (!data) return;
    const sourceColumn = colDef?.field;
    if (sourceColumn) {
      const additionalNotesField = sourceColumn === "additionalNotes" ? "additionalNotes" : sourceColumn === "endingComment" ? "endingComment" : sourceColumn === "financialReportComment" ? "financialReportComment" : "additionalNotesAccountantChange";
      const tnoteTypeId = sourceColumn === "additionalNotes" ? 1 : sourceColumn === "endingComment" ? 31 : sourceColumn === "financialReportComment" ? 41 : 11;

      const note = data[additionalNotesField] || '';
      if (!data.notes) data.notes = [];
      const existingNote = data.notes.find(item => item.tnoteTypeId === tnoteTypeId);

      if (existingNote !== undefined && existingNote) {
        if (note === '') {
          const index = data.notes.findIndex(n => n.id === existingNote.id);
          if (index !== -1) {
            data.notes.splice(index, 1);
          }
        } else {
          existingNote.note = note;
        }
      } else if (note !== '') {
        data.notes.push({
          id: null,
          note,
          contactId: data.id,
          tnoteTypeId,
          addedByContactId: contactId
        });
      }
    }

    data.isFromStartAccounting = true;

    const executePut = async (payload) => {
      const response = await fetch(urlClients + `/startAccounting/${payload.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Błąd serwera: ${response.status} ${text}`);
      }
      return await response.json();
    };

    let responseData;
    try {
      responseData = await executePut(data);
    } catch (error) {
      if (error.message && error.message.includes('Record has changed since last read')) {
        console.warn('Wykryto błąd wersjonowania w bazie (Record has changed since last read). Pobieranie świeżych danych...');
        const freshData = await fetchDataPromise(urlClients + "/" + data.id, null, null, console.error, token);
        if (freshData) {
          const mergedData = { ...freshData, ...data, isFromStartAccounting: true };
          responseData = await executePut(mergedData);
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }

    console.log('Aktualizowany rekord został wysłany do bazy danych:', responseData);

    const updatedRowData = addTooltipField(responseData);
    const rowNode = gridApi?.getRowNode(responseData.id.toString());
    if (rowNode) {
      rowNode.setData(updatedRowData);
    }

    setTableData(prev => prev.map(item => item.id === responseData.id ? updatedRowData : item));
    setTableData2(prev => prev.map(item => item.id === responseData.id ? updatedRowData : item));
    setTableData3(prev => prev.map(item => item.id === responseData.id ? updatedRowData : item));

    const headerName = colDef?.headerName || colDef?.field || 'Akcja';
    if (start) {
      logActivity(logActionType.CLIENTS_WAITING_FOR_START_ROW_EDIT + ", kolumna: '" + headerName + "', klient: " + responseData.companyWorkingName, userName, token);
    } else if (end) {
      logActivity(logActionType.CLIENTS_ENDED_COOPERATION + ", kolumna: '" + headerName + "', klient: " + responseData.companyWorkingName, userName, token);
    } else {
      logActivity(logActionType.CLIENTS_WAITING_FOR_ACCOUNTANT_ROW_EDIT + ", kolumna: '" + headerName + "', klient: " + responseData.companyWorkingName, userName, token);
    }
    return updatedRowData;
  };

  const handleRowDataChange = (params) => {
    if (!params || !params.data) return;
    const clientId = params.data.id;
    const currentQueue = requestQueueRef.current[clientId] || Promise.resolve();

    const nextQueue = currentQueue
      .then(() => processRowDataChange(params))
      .catch((error) => {
        console.error('Fetch error:', error);
        alert('Błąd podczas wysyłania rekordu: ' + error.message);
      });

    requestQueueRef.current[clientId] = nextQueue;
  };

  const addTooltipField = (item) => {
    let additionalNotes = '';
    let additionalNotesAccountantChange = '';
    let endingComment = '';
    let financialReportComment = '';
    let tooltipAdditionalNotes = '';
    let tooltipAdditionalNotesAccountantChange = '';
    let tooltipEndingComment = '';
    let tooltipFinancialReport = '';

    if (item.notes && Array.isArray(item.notes)) {
      item.notes.forEach(note => {
        const meta = [note.addedBy, note.noteDate].filter(Boolean).join(' ');
        const fullTooltip = note.note ? (meta ? `${note.note}\n(${meta})` : note.note) : '';

        if (note.tnoteTypeId === 1) {
          additionalNotes = note.note || '';
          tooltipAdditionalNotes = fullTooltip;
        } else if (note.tnoteTypeId === 11) {
          additionalNotesAccountantChange = note.note || '';
          tooltipAdditionalNotesAccountantChange = fullTooltip;
        } else if (note.tnoteTypeId === 31) {
          endingComment = note.note || '';
          tooltipEndingComment = fullTooltip;
        } else if (note.tnoteTypeId === 41) {
          financialReportComment = note.note || '';
          tooltipFinancialReport = fullTooltip;
        }
      });
    }

    return {
      ...item,
      additionalNotes: additionalNotes,
      additionalNotesAccountantChange: additionalNotesAccountantChange,
      endingComment: endingComment,
      financialReportComment: financialReportComment,
      tooltip: tooltipAdditionalNotes,
      tooltip1: tooltipAdditionalNotesAccountantChange,
      tooltip2: tooltipEndingComment,
      tooltip3: tooltipFinancialReport,
    };
  };


  const handleArchive = (params) => {
    const { data } = params;
    const currentDate = new Date().toISOString().split('T')[0]; // Dzisiejsza data w formacie YYYY-MM-DD

    data.startAccountingArchivedDate = currentDate;
    logActivity(logActionType.ARCHIVE_CLIENTS_WAITING_FOR_START_ROW + params.data.companyWorkingName, userName, token);
    handleRowDataChange(params);
    alert("Wiersz został zarchiwizowany");
    setTableData(prev => prev.filter(item => item.id !== data.id));
  };

  const handleArchiveEndedCooperation = (params) => {
    const { data } = params;
    const currentDate = new Date().toISOString().split('T')[0]; // Dzisiejsza data w formacie YYYY-MM-DD

    data.endedCooperationArchivedDate = currentDate;
    logActivity(logActionType.ARCHIVE_CLIENTS_ENDED_COOP_ROW + params.data.companyWorkingName, userName, token);
    handleRowDataChange(params);
    alert("Wiersz został zarchiwizowany");
    setTableData3(prev => prev.filter(item => item.id !== data.id));
  };

  const handleChange = (e, value) => {
    setIsArchiveView(false);
    setValue(value);
    if (value === "one") {
      fetchClientsNotArchived()
      setStart(true);
    }
    else if (value === "two") {
      fetchClientsWithAccountantChanged()
      setStart(false);
      setEnd(false);
    }
    else {
      fetchClientsWithEndedCooperation()
      setStart(false);
      setEnd(true)
    }
  };

  const handleArchiveAfterAccountantChange = (params) => {
    const { data } = params;
    const currentDate = new Date().toISOString().split('T')[0]; // Dzisiejsza data w formacie YYYY-MM-DD

    data.accountantChangeTableArchivedDate = currentDate;
    data.isAccountantChanged = false;
    logActivity(logActionType.ARCHIVE_CLIENTS_WAITING_FOR_ACCOUNTANT_ROW_EDIT + params.data.companyWorkingName, userName, token);
    handleRowDataChange(params);
    alert("Wiersz został zarchiwizowany");
    setTableData2(prev => prev.filter(item => item.id !== data.id));
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onChange = (e) => {
    const { value, id } = e.target
    // console.log(value,id)
    setFormData({ ...formData, [id]: value })
  }

  const handleFormSubmit = () => {
    formData.editedBy = userName;
    const activityType = logActionType.CLIENT_CARD_EDIT;

    if (formData.id) {
      fetch(urlClients + `/${formData.id}`, {
        method: "PUT",
        body: JSON.stringify(formData),
        headers: {
          'content-type': "application/json",
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (!response.ok) {
            if (response.status === 400) {
              throw new Error("Klient o podanym numerze NIP już istnieje w bazie");
            }
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return true;
        })
        .then(data => {
          handleClose();
          logActivity(`${activityType} ${formData.companyWorkingName}, NIP: ${formData.nip}`, userName, token);

          // Aktualizacja odpowiednich widoków w zależności od stanu archiwum
          if (isArchiveView) {
            switch (value) {
              case "one":
                fetchClientsArchived();
                break;
              case "two":
                fetchArchivedClientsWithAccountantChanged();
                break;
              case "three":
                fetchArchivedClientsWithEndedCooperation();
                break;
              default:
                break;
            }
          } else {
            switch (value) {
              case "one":
                fetchClientsNotArchived();
                break;
              case "two":
                fetchClientsWithAccountantChanged();
                break;
              case "three":
                fetchClientsWithEndedCooperation();
                break;
              default:
                break;
            }
          }
        })
        .catch(error => {
          alert(error.message);
          console.error('Error:', error);
        });
    }
  };

  const handleArchiveClick = () => {
    setIsLoading(true); // 🔹 Ustaw loading przed zmianą widoku

    if (isArchiveView) {
      switch (value) {
        case "one":
          fetchClientsNotArchived();
          break;
        case "two":
          fetchClientsWithAccountantChanged();
          break;
        case "three":
          fetchClientsWithEndedCooperation();
          break;
        default:
          break;
      }
      setIsArchiveView(false);
    } else {
      setIsArchiveView(true);

      switch (value) {
        case "one":
          fetchClientsArchived();
          break;
        case "two":
          fetchArchivedClientsWithAccountantChanged();
          break;
        case "three":
          fetchArchivedClientsWithEndedCooperation();
          break;
        default:
          break;
      }
    }
  };
  const clearFilters = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (api) {
      api.setFilterModel(null);

      if (api.applyColumnState) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else if (columnApi && columnApi.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { sort: null } });
      } else if (api.setSortModel) {
        api.setSortModel(null);
      }
    }
  }, []);

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12} sm={12} md={12}>
            <div className="App1">
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 2, mb: 1, gap: 1.5 }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  textColor="primary"
                  indicatorColor="primary"
                  aria-label="secondary tabs example"
                  sx={{
                    "& .MuiTabs-indicator": {
                      backgroundColor: darkTheme ? "#90caf9" : "#1976d2",
                      height: "3px",
                      borderRadius: "2px",
                      transition: "all 0.3s ease-in-out",
                    },
                  }}
                >
                  <Tab
                    value="one"
                    label="Klienci oczekujący na start"
                    sx={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: darkTheme ? "#fff" : "#555",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                        color: darkTheme ? "#90caf9" : "#1976d2",
                      },
                      "&.Mui-selected": {
                        color: darkTheme ? "#90caf9" : "#1976d2",
                        backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                      },
                    }}
                  />
                  <Tab
                    value="two"
                    label="Klienci oczekujący na zmianę księgowej"
                    sx={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: darkTheme ? "#fff" : "#555",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                        color: darkTheme ? "#90caf9" : "#1976d2",
                      },
                      "&.Mui-selected": {
                        color: darkTheme ? "#90caf9" : "#1976d2",
                        backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                      },
                    }}
                  />
                  <Tab
                    value="three"
                    label="Kończenie współpracy"
                    sx={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: darkTheme ? "#fff" : "#555",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                        color: darkTheme ? "#90caf9" : "#1976d2",
                      },
                      "&.Mui-selected": {
                        color: darkTheme ? "#90caf9" : "#1976d2",
                        backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                      },
                    }}
                  />
                </Tabs>
                {/* Przycisk przypisany do aktywnej zakładki */}
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Controls.PremiumButton
                    variant="glass"
                    label="Wyczyść"
                    onClick={clearFilters}
                    icon="fas fa-filter"
                  />
                  <Controls.PremiumButton
                    variant="purple"
                    label={isArchiveView ? "Zamknij" : "Archiwum"}
                    onClick={handleArchiveClick}
                    icon={isArchiveView ? "fas fa-folder-open" : "fas fa-archive"}
                  />
                </Box>
              </Box>
              <Box sx={{ m: '20px' }} />
              <div className="tableData">
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    rowData={isLoading ? [] : start ? tableData : end ? tableData3 : tableData2}
                    ref={gridRef}
                    getRowId={getRowId}
                    columnDefs={start ? columnDefs : end ? ended : others}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    suppressBrowserResizeObserver={true}
                    onCellValueChanged={handleRowDataChange}
                    animateRows={true}
                    rowSelection={false}
                    enableBrowserTooltips={false}
                    pagination={true}
                    paginationPageSize={50}
                    suppressSizeToFit={true}
                    headerHeight={80}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    localeText={MyCollections.localeTextPL}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <Box sx={{ m: '60px' }} />
        <FormDialog open={open} handleClose={() => handleClose()}
          onChange={onChange} handleFormSubmit={handleFormSubmit} setFormData={setFormData} formData={formData} />
        <Dialog
          open={financialStatementsDialogOpen}
          onClose={() => setFinancialStatementsDialogOpen(false)}
          fullWidth
          maxWidth="xl"
          disableEnforceFocus
          PaperProps={{
            style: {
              backgroundColor: darkTheme ? '#1e1e1e' : '#fff',
              minHeight: '80vh',
              overflow: 'visible',
            }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: darkTheme ? '#fff' : '#000' }}>
              Sprawozdanie finansowe
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setFinancialStatementsDialogOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Zamknij
            </Button>
          </Box>
          <DialogContent sx={{ p: 0, overflow: 'auto' }}>
            {financialStatementsDialogOpen && (
              <FinancialStatements
                inlineClientId={selectedClientId}
                popupParentOverride={document.body}
                initialSupervisorMode={roles?.includes('SUPERVISOR_ACCOUNTANT') || roles?.includes('SUPERVISOR')}
              />
            )}
          </DialogContent>
        </Dialog>
      </Paper>
    </>
  );
}

export default StartAccounting;